import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CategoryContext } from '../../Contexts/CategoryContext';
import { FaChevronRight, FaArrowLeft } from 'react-icons/fa';
import SEO from '../../Components/utils/SEO';

const Categories = () => {
    const { fetchCategoriesWithCounts } = useContext(CategoryContext);
    const [categoriesData, setCategoriesData] = useState([]);
    const [selectedMain, setSelectedMain] = useState(null);
    const [selectedSub, setSelectedSub] = useState(null);

    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const loadCategories = async () => {
            setLoading(true);
            const data = await fetchCategoriesWithCounts();
            setCategoriesData(data);
            setLoading(false);
        };
        loadCategories();
    }, []);

    // Navigate to products page with category filters
    const navigateToProducts = (main, sub = '', child = '') => {
        const params = new URLSearchParams();
        params.set('category', main);
        if (sub) params.set('subcategory', sub);
        if (child) params.set('childcategory', child);
        navigate(`/products-services?${params.toString()}`);
    };

    // Handle category card click
    const handleMainClick = (cat) => {
        setSelectedMain(cat);
        setSelectedSub(null);
        window.scrollTo(0, 0);
    };

    const handleSubClick = (sub, mainName) => {
        if (sub.children && sub.children.length > 0) {
            setSelectedSub({ ...sub, mainName });
            window.scrollTo(0, 0);
        } else {
            navigateToProducts(mainName, sub.name);
        }
    };

    const handleChildClick = (childName, subName, mainName) => {
        navigateToProducts(mainName, subName, childName);
    };

    const goBack = () => {
        if (selectedSub) {
            setSelectedSub(null);
        } else if (selectedMain) {
            setSelectedMain(null);
        }
        window.scrollTo(0, 0);
    };

    // Default banner gradients per category
    const bannerGradients = {
        'Food, Sweets & Drinks': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'Gift Baskets': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'Bouquets': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'Gift Items': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'Services': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'Event Accessories & Equipment': 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    };

    const getGradient = (name) => bannerGradients[name] || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

    const seoTitle = selectedSub
        ? `${selectedSub.name} categories`
        : selectedMain
            ? `${selectedMain.name} categories`
            : 'Browse categories';

    const seoDescription = selectedSub
        ? `Explore ${selectedSub.name} items on Chocowi by subcategory and type.`
        : selectedMain
            ? `Browse ${selectedMain.name} subcategories and discover curated products and services.`
            : 'Browse all Chocowi categories across products and services.';

    // Render loading skeleton
    if (loading) {
        return (
            <div className="categoriesPage">
                <SEO title={seoTitle} description={seoDescription} canonicalPath="/categories" />
                <section className="mainSection">
                    <div className="catPageHeader">
                        <h1>Categories</h1>
                        <p>Browse our collection of products and services</p>
                    </div>
                    <div className="catGrid">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="catCard skeleton">
                                <div className="catCardBanner"></div>
                                <div className="catCardBody"><div className="skeletonLine"></div></div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        );
    }

    // Child categories view
    if (selectedSub) {
        return (
            <div className="categoriesPage">
                <SEO title={seoTitle} description={seoDescription} canonicalPath="/categories" />
                <section className="mainSection">
                    <button className="catBackBtn" onClick={goBack}>
                        <FaArrowLeft /> Back to {selectedMain.name}
                    </button>

                    <div className="catSubBanner" style={{ background: getGradient(selectedMain.name) }}>
                        <div className="catSubBannerContent">
                            <span className="catSubBannerIcon">{selectedSub.icon || selectedMain.icon}</span>
                            <div>
                                <h2>{selectedSub.name}</h2>
                                <p>{selectedSub.productCount} items • {selectedSub.children?.length || 0} subcategories</p>
                            </div>
                        </div>
                        <button className="viewAllBtn" onClick={() => navigateToProducts(selectedSub.mainName, selectedSub.name)}>
                            View All Items <FaChevronRight />
                        </button>
                    </div>

                    <div className="catChildGrid">
                        {selectedSub.children.map((child, idx) => (
                            <div key={idx} className="catChildCard" onClick={() => handleChildClick(child.name, selectedSub.name, selectedSub.mainName)}>
                                <div className="catChildInfo">
                                    <span className="catChildName">{child.name}</span>
                                    <span className="catChildCount">{child.productCount || 0} items</span>
                                </div>
                                <FaChevronRight className="catChildArrow" />
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        );
    }

    // Subcategories view
    if (selectedMain) {
        return (
            <div className="categoriesPage">
                <SEO title={seoTitle} description={seoDescription} canonicalPath="/categories" />
                <section className="mainSection">
                    <button className="catBackBtn" onClick={goBack}>
                        <FaArrowLeft /> All Categories
                    </button>

                    <div className="catMainBanner" style={{
                        background: selectedMain.banner ? `url(${selectedMain.banner}) center/cover` : getGradient(selectedMain.name)
                    }}>
                        <div className="catMainBannerOverlay">
                            <span className="catMainBannerIcon">{selectedMain.icon}</span>
                            <h1>{selectedMain.name}</h1>
                            <p>{selectedMain.productCount} products • {selectedMain.subcategories?.length || 0} subcategories</p>
                            <div className="catMainBannerMeta">
                                <span className="metaBadge">{selectedMain.scope === 'international' ? '🌍 International' : '📍 Local'}</span>
                                <span className="metaBadge">{selectedMain.type === 'service' ? '🛎️ Services' : '📦 Products'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="catSubGrid">
                        {selectedMain.subcategories?.map((sub, idx) => (
                            <div key={idx} className="catSubCard" onClick={() => handleSubClick(sub, selectedMain.name)}>
                                <div className="catSubLeft">
                                    {sub.icon && <span className="catSubIcon">{sub.icon}</span>}
                                    <div className="catSubInfo">
                                        <span className="catSubName">{sub.name}</span>
                                        <span className="catSubCount">
                                            {sub.productCount || 0} items
                                            {sub.children?.length > 0 && ` • ${sub.children.length} types`}
                                        </span>
                                    </div>
                                </div>
                                <FaChevronRight className="catSubArrow" />
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        );
    }

    // Main categories grid view
    return (
        <div className="categoriesPage">
            <SEO title={seoTitle} description={seoDescription} canonicalPath="/categories" />
            <section className="mainSection">
                <div className="catPageHeader">
                    <h1>Browse Categories</h1>
                    <p>Explore our wide range of products and services</p>
                </div>

                <div className="catGrid">
                    {categoriesData.map(cat => (
                        <div key={cat._id} className="catCard" onClick={() => handleMainClick(cat)}>
                            <div className="catCardBanner" style={{
                                background: cat.banner ? `url(${cat.banner}) center/cover` : getGradient(cat.name)
                            }}>
                                <div className="catCardBannerOverlay">
                                    <span className="catCardIcon">{cat.icon}</span>
                                </div>
                            </div>
                            <div className="catCardBody">
                                <h3 className="catCardTitle">{cat.name}</h3>
                                <div className="catCardMeta">
                                    <span className="catCardCount">{cat.productCount || 0} items</span>
                                    <span className="catCardSubs">{cat.subcategories?.length || 0} subcategories</span>
                                </div>
                                <div className="catCardBadges">
                                    <span className={`catCardBadge ${cat.scope}`}>
                                        {cat.scope === 'international' ? '🌍' : '📍'} {cat.scope}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default Categories;